import React, { createContext, useContext, useReducer, useCallback, useRef, useEffect } from 'react';
import type { DemoState, DemoAction, DemoStep, DemoScript, DemoDecisionConfig, AgentMessage } from '../../types/demo';

const initialState: DemoState = {
  phase: 'idle',
  scriptId: null,
  steps: [],
  currentStepIndex: -1,
  cursorPosition: { x: -100, y: -100 },
  cursorTarget: null,
  cursorVisible: false,
  audioState: 'idle',
  subtitleText: '',
  highlightRect: null,
  activeDecision: null,
  agentConversation: [],
  agentLoading: false,
};

function demoReducer(state: DemoState, action: DemoAction): DemoState {
  switch (action.type) {
    case 'LOAD_SCRIPT':
      return {
        ...initialState,
        scriptId: action.scriptId,
        steps: action.steps,
        phase: 'idle',
      };

    case 'START':
      return {
        ...state,
        phase: 'intro',
        currentStepIndex: 0,
        cursorVisible: true,
      };

    case 'PAUSE':
      return { ...state, phase: 'paused' };

    case 'RESUME':
      return {
        ...state,
        phase: state.activeDecision ? 'decision' : 'playing',
      };

    case 'STOP':
      return {
        ...initialState,
        scriptId: state.scriptId,
        steps: state.steps,
      };

    case 'ADVANCE_STEP': {
      const nextIndex = state.currentStepIndex + 1;
      if (nextIndex >= state.steps.length) {
        return { ...state, phase: 'complete', activeDecision: null, subtitleText: '' };
      }
      return {
        ...state,
        currentStepIndex: nextIndex,
        phase: 'playing',
        activeDecision: null,
        subtitleText: '',
        audioState: 'idle',
      };
    }

    case 'JUMP_TO_STEP': {
      const idx = state.steps.findIndex((s) => s.id === action.stepId);
      if (idx === -1) return state;
      return {
        ...state,
        currentStepIndex: idx,
        phase: 'playing',
        activeDecision: null,
        subtitleText: '',
        audioState: 'idle',
      };
    }

    case 'SET_CURSOR':
      return { ...state, cursorPosition: action.position };

    case 'SET_CURSOR_TARGET':
      return { ...state, cursorTarget: action.target };

    case 'SET_CURSOR_VISIBLE':
      return { ...state, cursorVisible: action.visible };

    case 'SET_AUDIO_STATE':
      return { ...state, audioState: action.state };

    case 'SET_SUBTITLE':
      return { ...state, subtitleText: action.text };

    case 'SET_HIGHLIGHT':
      return { ...state, highlightRect: action.rect };

    case 'ENTER_DECISION':
      return { ...state, phase: 'decision', activeDecision: action.decision };

    case 'SELECT_DECISION': {
      const idx = state.steps.findIndex((s) => s.id === action.nextStepId);
      if (idx === -1) return { ...state, phase: 'playing', activeDecision: null };
      return {
        ...state,
        currentStepIndex: idx,
        phase: 'playing',
        activeDecision: null,
        subtitleText: '',
        audioState: 'idle',
      };
    }

    case 'ENTER_AGENT_QA':
      return { ...state, phase: 'agent-qa' };

    case 'EXIT_AGENT_QA':
      return { ...state, phase: 'playing', agentConversation: [], agentLoading: false };

    case 'ADD_AGENT_MESSAGE':
      return { ...state, agentConversation: [...state.agentConversation, action.message] };

    case 'SET_AGENT_LOADING':
      return { ...state, agentLoading: action.loading };

    case 'SET_PHASE':
      return { ...state, phase: action.phase };

    default:
      return state;
  }
}

interface DemoContextValue {
  state: DemoState;
  dispatch: React.Dispatch<DemoAction>;
  /** Load a demo script */
  loadScript: (script: DemoScript) => void;
  /** Start the demo from step 0 */
  start: () => void;
  /** Pause the demo */
  pause: () => void;
  /** Resume from pause */
  resume: () => void;
  /** Stop and reset the demo */
  stop: () => void;
  /** Advance to next step */
  advanceStep: () => void;
  /** Jump to a specific step by ID */
  jumpToStep: (stepId: string) => void;
  /** Get current step */
  currentStep: DemoStep | null;
}

const DemoContext = createContext<DemoContextValue | null>(null);

export const DemoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(demoReducer, initialState);

  const loadScript = useCallback((script: DemoScript) => {
    dispatch({ type: 'LOAD_SCRIPT', scriptId: script.id, steps: script.steps });
  }, []);

  const start = useCallback(() => dispatch({ type: 'START' }), []);
  const pause = useCallback(() => dispatch({ type: 'PAUSE' }), []);
  const resume = useCallback(() => dispatch({ type: 'RESUME' }), []);
  const stop = useCallback(() => dispatch({ type: 'STOP' }), []);
  const advanceStep = useCallback(() => dispatch({ type: 'ADVANCE_STEP' }), []);
  const jumpToStep = useCallback((stepId: string) => dispatch({ type: 'JUMP_TO_STEP', stepId }), []);

  const currentStep =
    state.currentStepIndex >= 0 && state.currentStepIndex < state.steps.length
      ? state.steps[state.currentStepIndex]
      : null;

  return (
    <DemoContext.Provider
      value={{ state, dispatch, loadScript, start, pause, resume, stop, advanceStep, jumpToStep, currentStep }}
    >
      {children}
    </DemoContext.Provider>
  );
};

export function useDemo(): DemoContextValue {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error('useDemo must be used within a DemoProvider');
  return ctx;
}
